import React, { useEffect, useState } from "react";
import { Papercontainer } from "@rewardle/apps.ui.papercontainer.papercontainer";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import styled from "@emotion/styled";
import { Link as LinkRouter } from "react-router-dom";
import { BusinessContext } from "@rewardle/apps.auth.businessauthorization.businessauthorization";
import FormControl from "@mui/material/FormControl";
import { OutlinedInput as MuiOutlinedInput } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import FormHelperText from "@mui/material/FormHelperText";
import TablePagination from "@mui/material/TablePagination";
import axios from "axios";
import {
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableHead,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  Typography as MuiTypography,
  Button as MuiButton
} from "@mui/material";
import {
  downloadPdf,
  formatFullDate,
  formatSettlementDate
} from "../../Utils/commonFunction";
import SearchIcon from "../../assets/icons/search.png";
import Loader from "../../components/loader/loader";
import DownloadIcon from "../../assets/icons/download.png";

const Typography = styled(MuiTypography)``;
const TableCell = styled(MuiTableCell)``;
const TableCellHeading = styled(MuiTableCell)`
  color: var(--light-text-primary, #192f67);
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.17px;
  width: 20px;
`;

const TableCellColumn = styled(MuiTableCell)`
color: var(--light-text-secondary, rgba(0, 0, 0, 0.60));
font-family: Montserrat,sans-serif;
font-size: 14px;
font-style: normal;
font-weight: 400;
line - height: normal;
`;

const TableRow = styled(MuiTableRow)``;

const CustomTableRow = styled(MuiTableRow)`
  &:hover {
    background-color: transparent;
  }
`;

const OutlinedInput = styled(MuiOutlinedInput)`
  height: 36px;
  flex-shrink: 0;
  border: 1px solid #d4d5d6;
  border-radius: 28px;
`;

const CustomTablePagination: any = styled(TablePagination)`
  .MuiTablePagination-selectLabel {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
  }
`;

interface LabelDisplayedRowsArgs {
  from: number;
  to: number;
  count: number;
}

const Settlement = () => {
  let currentBusiness: any = localStorage?.getItem("currentBusiness");
  const state: any = {
    token: localStorage?.getItem("businessAccessToken"),
    currentBusiness: currentBusiness ? JSON.parse(currentBusiness) : null
  };
  const [listOfSettlements, setListOfSettlements] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [isBusinessIdChange, setIsBusinessIdChange] = useState(false);

  const handleSettlementDataWithPagination = (
    response: {
      data: { Items: string | any[]; Count: any; LastEvaluatedKey: any };
    },
    page: number,
    rowsPerPage: number
  ) => {
    let oldData = [...listOfSettlements];
    let startIndex = rowsPerPage * (page + 1) - rowsPerPage;
    let endIndex = rowsPerPage * (page + 1);
    const slicedData = response?.data?.Items.slice(startIndex, endIndex);
    let newIndexData = {
      Count: response?.data?.Count,
      Items: slicedData,
      LastEvaluatedKey: response?.data?.LastEvaluatedKey
    };
    oldData.push(newIndexData);
    setListOfSettlements(oldData);
    return null;
  };

  const getSettlementsList = (page = currentPage) => {
    setIsLoading(true);

    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${
        process.env.REACT_APP_API_BASE_URL
      }/rewardlepay/business/statements?businessid=${
        state?.currentBusiness?.BusinessId
      }&PageSize=${rowsPerPage * (page + 1)}`,
      headers: {
        Authorization: "Bearer " + state?.token
      }
    };

    axios
      .request(config)
      .then((response) => {
        handleSettlementDataWithPagination(response, page, rowsPerPage);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setCurrentPage(0);
    setRowsPerPage(10);
    setListOfSettlements([]);
    setIsBusinessIdChange(!isBusinessIdChange);
  }, [state?.currentBusiness?.BusinessId]);

  useEffect(() => {
    if (rowsPerPage) getSettlementsList();
  }, [rowsPerPage, isBusinessIdChange]);

  const handleChangeRowsPerPage = (event?: any) => {
    setRowsPerPage(parseInt(event?.target?.value, 10));
    setCurrentPage(0);
    setListOfSettlements([]);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    if (newPage > currentPage) {
      getSettlementsList(newPage);
    } else {
      let oldData = [...listOfSettlements];
      const slicedData = oldData?.slice(0, newPage + 1);
      setListOfSettlements(slicedData);
    }
    setCurrentPage(newPage);
  };

  const labelDisplayedRows = ({ from, to, count }: LabelDisplayedRowsArgs) => {
    if (listOfSettlements[currentPage]?.LastEvaluatedKey) {
      return `${from}-${to} of more than ${count}`;
    } else {
      return `${from}-${to} of ${count}`;
    }
  };

  return (
    <React.Fragment>
      <Breadcrumbs
        heading="Settlements"
        subHeading="Your daily Settlements"
        docs={null}
      />

      <Papercontainer elevation={0}>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div style={{ width: "100%", overflowX: "auto" }}>
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label="a dense table"
              >
                <TableHead>
                  <TableRow>
                    <TableCellHeading align="left">Date</TableCellHeading>
                    <TableCellHeading align="left">Purchases</TableCellHeading>
                    <TableCellHeading align="left">Refunds</TableCellHeading>
                    <TableCellHeading align="left">
                      Transaction Fees
                    </TableCellHeading>
                    <TableCellHeading align="left">Settlement</TableCellHeading>
                    <TableCellHeading align="left"></TableCellHeading>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {listOfSettlements?.length > 0 ? (
                    listOfSettlements[currentPage]?.Items?.map(
                      (row: any, index: any) => (
                        <CustomTableRow key={index}>
                          <TableCellColumn align="left" width="25%">
                            {row?.EndDate
                              ? formatSettlementDate(row?.EndDate)
                              : "-"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.TotalTransactionAmount
                              ? `$${(
                                  row?.TotalTransactionAmount / 100
                                )?.toFixed(2)}`
                              : "$0"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.TotalRefundAmount
                              ? `$${(row?.TotalRefundAmount / 100)?.toFixed(2)}`
                              : "$0"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.TotalTransactionFee
                              ? `$${(row?.TotalTransactionFee / 100)?.toFixed(
                                  2
                                )}`
                              : "$0"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.SettlementAmount
                              ? `$${(row?.SettlementAmount / 100)?.toFixed(2)}`
                              : "$0"}
                          </TableCellColumn>
                          <TableCellColumn align="left">
                            {row?.TextInvoicePdf ? (
                              <img
                                src={DownloadIcon}
                                alt=""
                                onClick={() => downloadPdf(row?.TextInvoicePdf)}
                                style={{
                                  cursor: "pointer"
                                }}
                              />
                            ) : (
                              "-"
                            )}
                          </TableCellColumn>
                        </CustomTableRow>
                      )
                    )
                  ) : (
                    <TableCell
                      colSpan={5}
                      align="center"
                      style={{ borderBottom: "none" }}
                    >
                      <Typography variant="body1" mt={2}>
                        {"No Data Available"}
                      </Typography>
                    </TableCell>
                  )}
                </TableBody>
              </Table>
            </div>
            {listOfSettlements?.length > 0 && (
              <CustomTablePagination
                rowsPerPageOptions={[10, 50, 100]}
                component="div"
                count={listOfSettlements[currentPage]?.Count}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                nextIconButtonProps={{
                  disabled: !listOfSettlements[currentPage]?.LastEvaluatedKey
                }}
                labelDisplayedRows={labelDisplayedRows}
              />
            )}
          </>
        )}
      </Papercontainer>
    </React.Fragment>
  );
};

export default Settlement;
